import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

interface Booking {
  date: string;
  session: string;
  client: string;
  chips: number;
}

interface Props {
  bookings: Booking[];
  primaryColor: string;
}

export const TalentWalletChartSection: React.FC<Props> = ({
  bookings,
  primaryColor,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const chartData = bookings.map((b) => ({
    date: b.date?.slice(5) || '-',
    earnings: typeof b.chips === 'number' ? b.chips : 0,
  }));

  return (
    <Accordion
      defaultExpanded
      sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: COLORS.text }} />}
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          backgroundColor: 'rgba(255,255,255,0.06)',
        }}
      >
        <Typography fontWeight={600} fontSize={{ xs: 15, sm: 16 }} sx={{ color: COLORS.text }}>
          📈 Earnings Trend
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: { xs: 2, sm: 3 },
          pb: 3,
          pt: 1,
          backgroundColor: COLORS.panel,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            backgroundColor: 'rgba(255,255,255,0.06)',
            border: `1px solid ${COLORS.border}`,
            height: isMobile ? 280 : 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                <XAxis
                  dataKey="date"
                  fontSize={12}
                  stroke={COLORS.muted}
                  tick={{ fill: COLORS.text }}
                />
                <YAxis
                  fontSize={12}
                  stroke={COLORS.muted}
                  tick={{ fill: COLORS.text }}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 13,
                    borderRadius: 8,
                    backgroundColor: COLORS.panel,
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  }}
                  labelStyle={{ fontWeight: 600, color: COLORS.text }}
                />
                <Line
                  type="monotone"
                  dataKey="earnings"
                  stroke={primaryColor}
                  strokeWidth={2}
                  dot={{ r: 3, fill: primaryColor }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box textAlign="center">
              <Typography
                variant="body2"
                sx={{ color: COLORS.muted }}
                fontSize={14}
              >
                No earnings data available.
              </Typography>
            </Box>
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};
